/**
 * Every campaign as a card: its name, window and state, three figures for the
 * last seven days, and a chart of that week's views per day. A segmented
 * control above narrows the grid to one state.
 *
 * While the organisation is waiting on credits a campaign also carries a quiet
 * "sem acompanhamento" mark rather than a zero (§5.4): a zero would be
 * indistinguishable from a campaign that ran and collected nothing.
 */

import { Link } from '@tanstack/react-router';
import { Megaphone } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { Area, AreaChart, XAxis } from 'recharts';

import { Absent } from '../../../chrome/measure';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { getLocale } from '../../../i18n';
import { compact, integer } from '../../../i18n/format';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '../../../ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../ui/chart';
import { cn } from '../../../ui/utils';
import { accountsPath } from '../../control/paths';
import { SegmentedControl } from '../../data/publications/SegmentedControl';
import type { CampaignDay, CampaignSummary } from '../api/schemas';
import { trackedAccounts, useAcquisitionState, useFundedWindow } from '../balance';
import { tt } from '../i18n';
import { hm } from './messages';
import { useCampaigns } from './useCampaigns';

type Filter = 'ALL' | CampaignSummary['state'];

const STATE_LABEL: Record<CampaignSummary['state'], () => string> = {
  ACTIVE: () => hm('status.collecting'),
  PAUSED: () => hm('status.paused'),
  ARCHIVED: () => hm('status.archived'),
};

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(getLocale(), { dateStyle: 'medium' }).format(new Date(iso));
}

const dayLabel = (day: string): string =>
  new Intl.DateTimeFormat(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));

/** The raw IANA identifier (`America/Sao_Paulo`) is a machine key, not a
 *  label a tenant reads; `timeZoneName: 'long'` gives the locale's own name. */
function formatTimezone(timezone: string): string {
  const parts = new Intl.DateTimeFormat(getLocale(), {
    timeZone: timezone,
    timeZoneName: 'long',
  }).formatToParts(new Date());
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? timezone;
}

function windowOf(campaign: CampaignSummary): string {
  if (campaign.active_from === null || campaign.active_to === null) return hm('card.noWindow');
  return hm('card.window', {
    from: formatDate(campaign.active_from),
    to: formatDate(campaign.active_to),
  });
}

/** The zone most of the organisation's campaigns share: the one no card needs to name. */
function commonTimezone(campaigns: readonly CampaignSummary[]): string | null {
  const counts = new Map<string, number>();
  for (const campaign of campaigns) {
    counts.set(campaign.timezone, (counts.get(campaign.timezone) ?? 0) + 1);
  }
  let common: string | null = null;
  for (const [zone, count] of counts)
    if (common === null || count > counts.get(common)!) common = zone;
  return common;
}

function filterCampaigns(campaigns: readonly CampaignSummary[], filter: Filter): CampaignSummary[] {
  return campaigns.filter((campaign) => filter === 'ALL' || campaign.state === filter);
}

function Figure(props: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex min-w-0 flex-col justify-between gap-0.5">
      <dt className="text-caption leading-tight text-muted-foreground">{props.label}</dt>
      <dd data-numeric className="text-title-sm">
        {props.children}
      </dd>
    </div>
  );
}

function WeekChart(props: { days: readonly CampaignDay[] }): ReactNode {
  const locale = getLocale();
  if (props.days.length === 0) {
    return <p className="text-caption text-muted-foreground">{hm('chart.noAccounts')}</p>;
  }
  if (props.days.every((day) => day.views === null)) {
    return <p className="text-caption text-muted-foreground">{hm('chart.noReading')}</p>;
  }
  const config = {
    views: { label: hm('chart.views'), color: 'var(--primary-ink)' },
    posts: { label: hm('chart.posts'), color: 'var(--muted-foreground)' },
  } satisfies ChartConfig;
  return (
    <ChartContainer
      config={config}
      role="img"
      aria-label={hm('chart.label')}
      initialDimension={{ width: 300, height: 64 }}
      className="aspect-auto h-16 w-full"
    >
      <AreaChart data={[...props.days]} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <XAxis dataKey="day" hide />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              labelFormatter={(_label, payload) => {
                const day: unknown = payload[0]?.payload?.day;
                return typeof day === 'string' ? dayLabel(day) : null;
              }}
              formatter={(value, _name, item) => {
                const posts: unknown = item.payload?.posts;
                return (
                  <div className="flex w-full flex-col gap-0.5">
                    <span className="flex justify-between gap-4">
                      <span className="text-muted-foreground">{hm('chart.views')}</span>
                      <span data-numeric>
                        {typeof value === 'number' ? compact(value, locale) : '—'}
                      </span>
                    </span>
                    <span className="flex justify-between gap-4">
                      <span className="text-muted-foreground">{hm('chart.posts')}</span>
                      <span data-numeric>
                        {typeof posts === 'number' ? integer(posts, locale) : '—'}
                      </span>
                    </span>
                  </div>
                );
              }}
            />
          }
        />
        <Area
          dataKey="views"
          type="monotone"
          stroke="var(--color-views)"
          fill="var(--color-views)"
          fillOpacity={0.15}
          strokeWidth={2}
          connectNulls
          isAnimationActive={false}
        />
      </AreaChart>
    </ChartContainer>
  );
}

function CampaignCard(props: {
  campaign: CampaignSummary;
  accounts: number | null;
  showTimezone: boolean;
  waitingOnCredits: boolean;
}): ReactNode {
  const { campaign } = props;
  const locale = getLocale();
  return (
    <Card className="h-full gap-4 py-4">
      <CardHeader className="gap-1 px-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="min-w-0 font-medium">
            <Link
              to={accountsPath(campaign.id)}
              className="block truncate rounded-sm outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
            >
              {campaign.name}
            </Link>
          </h3>
          <Badge
            variant="secondary"
            className={cn(
              'shrink-0',
              campaign.state === 'ACTIVE' && 'bg-primary-quiet text-primary-quiet-foreground',
            )}
          >
            {STATE_LABEL[campaign.state]()}
          </Badge>
        </div>
        <CardDescription className="text-caption">
          {props.showTimezone && (
            <>
              {formatTimezone(campaign.timezone)}
              {/* eslint-disable-next-line no-restricted-syntax -- middot separator, identical in both locales */}
              {' · '}
            </>
          )}
          {windowOf(campaign)}
        </CardDescription>
        {props.waitingOnCredits && (
          <span
            data-testid="nothing-fetched-campaign"
            className="text-caption text-muted-foreground"
          >
            {tt('campaigns.notCollected')}
          </span>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 px-4">
        <dl className="grid grid-cols-3 gap-2">
          <Figure label={hm('figure.accounts')}>
            {props.accounts === null ? (
              <Absent reason="not_measured" />
            ) : (
              integer(props.accounts, locale)
            )}
          </Figure>
          <Figure label={hm('figure.reach')}>
            {campaign.reach_7d === null ? (
              <Absent reason="not_measured" />
            ) : (
              compact(campaign.reach_7d, locale)
            )}
          </Figure>
          <Figure label={hm('figure.posts')}>
            {campaign.posts_7d === null ? (
              <Absent reason="not_measured" />
            ) : (
              integer(campaign.posts_7d, locale)
            )}
          </Figure>
        </dl>
        <WeekChart days={campaign.daily} />
      </CardContent>
    </Card>
  );
}

/** `onCreate` is absent for a caller who may not write: the empty state then
 *  offers nothing rather than a disabled button (platform design §17.15). */
export function CampaignCards(props: {
  tenantId: string;
  onCreate?: () => void;
  /** A writer with no free slot: the empty state says why it offers no create. */
  createBlocked?: boolean;
}): ReactNode {
  const query = useCampaigns(props.tenantId);
  const acquisitionState = useAcquisitionState(props.tenantId);
  const fundedWindow = useFundedWindow(props.tenantId);
  const [filter, setFilter] = useState<Filter>('ALL');
  const headingId = useId();

  const tracked = fundedWindow.data === undefined ? null : trackedAccounts(fundedWindow.data);
  function accountsFor(campaignId: string): number | null {
    return tracked?.filter((account) => account.campaign_id === campaignId).length ?? null;
  }

  const createAction =
    props.onCreate !== undefined ? (
      <Button onClick={props.onCreate}>{tt('newCampaign.submit')}</Button>
    ) : props.createBlocked === true ? (
      <p className="text-body-sm text-muted-foreground">{hm('newCampaign.noSlot')}</p>
    ) : undefined;

  return (
    <QueryStates
      query={query}
      isEmpty={(campaigns) => campaigns.length === 0}
      empty={
        <EmptyState
          icon={Megaphone}
          title={hm('empty.title')}
          description={hm('empty.body')}
          // `newCampaign.submit`, not `home.newCampaign`: the header action
          // already carries that name, and two buttons sharing one accessible
          // name are unreachable by name for either a screen reader or `getByRole`.
          action={createAction}
        />
      }
    >
      {(campaigns) => {
        const common = commonTimezone(campaigns);
        const shown = filterCampaigns(campaigns, filter);
        return (
          <section aria-labelledby={headingId} className="flex flex-col gap-4">
            <h2 id={headingId} className="sr-only">
              {hm('section.campaigns')}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <SegmentedControl<Filter>
                label={hm('filter.label')}
                value={filter}
                onChange={setFilter}
                options={[
                  { value: 'ALL', label: hm('filter.all') },
                  { value: 'ACTIVE', label: hm('status.collecting') },
                  { value: 'PAUSED', label: hm('status.paused') },
                  { value: 'ARCHIVED', label: hm('status.archived') },
                ]}
              />
            </div>
            {shown.length === 0 ? (
              <p className="rounded-lg border border-dashed p-6 text-center text-body-sm text-muted-foreground">
                {hm('filter.empty')}
              </p>
            ) : (
              <ul
                aria-labelledby={headingId}
                className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
              >
                {shown.map((campaign) => (
                  <li key={campaign.id}>
                    <CampaignCard
                      campaign={campaign}
                      accounts={accountsFor(campaign.id)}
                      showTimezone={campaign.timezone !== common}
                      waitingOnCredits={acquisitionState === 'waiting-on-credits'}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      }}
    </QueryStates>
  );
}
